// src/components/Modal.jsx
import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

/**
 * A mobile-optimized modal component
 * 
 * @param {Object} props Component props
 * @param {boolean} props.isOpen Whether the modal is currently open
 * @param {Function} props.onClose Function called when the modal is closed
 * @param {string} props.title Modal title text
 * @param {React.ReactNode} props.children Modal content
 * @param {string} props.size Optional size variant ('sm', 'md', 'lg', 'full')
 * @param {boolean} props.mobileFullscreen Whether the modal should take full screen on mobile devices
 * @param {boolean} props.preventBackdropClose Whether to prevent closing when clicking the backdrop
 * @param {string} props.className Additional CSS classes for the modal container
 */
const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  size = 'md',
  mobileFullscreen = true,
  preventBackdropClose = false,
  className = ''
}) => {
  // Handle escape key to close modal
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape' && !preventBackdropClose) {
      onClose();
    }
  }, [onClose, preventBackdropClose]);
  
  useEffect(() => {
    if (!isOpen) return;
    
    document.addEventListener('keydown', handleEscape);
    
    // Prevent scrolling of background content
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, handleEscape]);
  
  if (!isOpen) return null;
  
  // Handle clicks outside the modal to close it
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !preventBackdropClose) {
      onClose();
    }
  };
  
  // Determine size class based on prop
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    full: 'max-w-full w-full h-full m-0 rounded-none'
  };
  
  // Determine mobile styling
  const mobileClass = mobileFullscreen 
    ? 'w-full h-full sm:h-auto sm:max-h-[90vh] rounded-none sm:rounded-lg m-0 sm:m-4' 
    : 'max-h-[90vh] rounded-lg m-4';
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        className={`
          relative bg-white shadow-xl overflow-hidden
          ${mobileClass}
          ${sizeClasses[size] || sizeClasses.md}
          ${className}
        `}
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b">
          <h3 
            id="modal-title" 
            className="text-base sm:text-lg font-medium text-gray-900 pr-8"
          >
            {title}
          </h3>
          <button
            onClick={onClose}
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-500 focus:outline-none mobile-touch-target"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className={`
          p-4 sm:p-6 overflow-y-auto
          ${mobileFullscreen 
            ? 'h-[calc(100vh-4rem)] sm:max-h-[calc(90vh-5rem)]' 
            : 'max-h-[calc(90vh-5rem)]'
          }
        `}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;